import type {
  CreateMiniLogRequestDto,
  MiniLogDataInfo,
  SaveMiniLogChunkDto,
  SaveMiniLogChunkBatchDto,
  UpdateMiniLogInfoDto,
  MiniGasReading,
  MiniCalibrationLog,
  MiniGasAlarmLog,
  MiniEventLog,
  MiniConfigurationLog,
} from '@packages/types/device/mini/log-dto';

/*
 * Project: bellcloud
 * File: apps/frontend/src/api/miniLog.ts
 * Author: SWAN
 * Created: 2025-01-XX
 * Description: MINI 로그 데이터 API
 */

import { apiHelpers, extractApiData } from '../../core/api.helpers';

export type {
  CreateMiniLogRequestDto,
  MiniLogDataInfo,
  SaveMiniLogChunkDto,
  SaveMiniLogChunkBatchDto,
  UpdateMiniLogInfoDto,
  MiniGasReading,
  MiniCalibrationLog,
  MiniGasAlarmLog,
  MiniEventLog,
  MiniConfigurationLog,
} from '@packages/types/device/mini/log-dto';

/**
 * 로그 요청 생성
 */
export const createLogRequest = async (dto: CreateMiniLogRequestDto): Promise<MiniLogDataInfo> => {
  const response = await apiHelpers.post<MiniLogDataInfo>('miniLogs.createRequest', dto);
  return extractApiData(response);
};

/**
 * 로그 메타데이터 업데이트
 */
export const updateLogInfo = async (
  logId: string,
  dto: UpdateMiniLogInfoDto
): Promise<MiniLogDataInfo> => {
  const response = await apiHelpers.patch<MiniLogDataInfo>('miniLogs.updateInfo', dto, { logId });
  return extractApiData(response);
};

/**
 * 청크 저장
 */
export const saveLogChunk = async (dto: SaveMiniLogChunkDto): Promise<any> => {
  const response = await apiHelpers.post('miniLogs.saveChunk', dto);
  return extractApiData(response);
};

/**
 * 청크 배치 저장 (성능 최적화)
 */
export const saveLogChunkBatch = async (dto: SaveMiniLogChunkBatchDto): Promise<number> => {
  // 배치 저장은 시간이 오래 걸릴 수 있으므로 타임아웃을 120초로 설정
  const response = await apiHelpers.post<number>(
    'miniLogs.saveChunkBatch',
    dto,
    {},
    {
      timeout: 120000, // 120초 (2분)
    }
  );
  return extractApiData(response);
};

/**
 * 로그 정보 조회
 */
export const getLogInfo = async (logId: string): Promise<MiniLogDataInfo> => {
  const response = await apiHelpers.get<MiniLogDataInfo>('miniLogs.getLogInfo', { logId });
  return extractApiData(response);
};

/**
 * 기기별 로그 목록 조회
 */
export const getLogsByDevice = async (
  serialNumber: string,
  limit?: number
): Promise<MiniLogDataInfo[]> => {
  const queryParams = limit ? { limit } : undefined;
  const response = await apiHelpers.get<MiniLogDataInfo[]>(
    'miniLogs.getLogsByDevice',
    { serialNumber },
    queryParams
  );
  return extractApiData(response);
};

/**
 * 누락된 청크 조회
 */
export const getMissingChunks = async (logId: string): Promise<number[]> => {
  const response = await apiHelpers.get<number[]>('miniLogs.getMissingChunks', { logId });
  return extractApiData(response);
};

/**
 * 로그 완료 처리 (서버에서 비동기 파싱 시작 — Lite와 동일)
 */
export const completeLog = async (logId: string): Promise<MiniLogDataInfo> => {
  const response = await apiHelpers.post<MiniLogDataInfo>(
    'miniLogs.completeLog',
    undefined,
    {
      logId,
    },
    {
      timeout: 120000,
    }
  );
  return extractApiData(response);
};

/**
 * 로그 파싱 (수동·재시도용 — 일반 다운로드 플로우는 completeLog가 비동기 파싱을 시작함)
 */
export const parseLog = async (logId: string): Promise<any> => {
  try {
    const response = await apiHelpers.post(
      'miniLogs.parseLog',
      undefined,
      { logId },
      {
        timeout: 600000,
      }
    );
    console.log('✅ [parseLog] API 응답:', {
      logId,
      status: response.status,
      data: response.data,
    });
    return extractApiData(response);
  } catch (error: unknown) {
    const axiosErr = error as { message?: string; response?: { data?: unknown; status?: number } };
    console.error('❌ [parseLog] API 에러:', {
      logId,
      error,
      message: error instanceof Error ? error.message : axiosErr.message,
      response: axiosErr.response?.data,
      status: axiosErr.response?.status,
    });
    throw error;
  }
};

/**
 * 로그 실패 처리
 */
export const failLog = async (logId: string, errorMessage: string): Promise<MiniLogDataInfo> => {
  const response = await apiHelpers.post<MiniLogDataInfo>(
    'miniLogs.failLog',
    { error_message: errorMessage },
    { logId },
    {
      timeout: 60000,
    }
  );
  return extractApiData(response);
};

/**
 * Gas Reading 데이터 조회
 */
export const getGasReadings = async (
  logId: string,
  limit?: number,
  offset?: number,
  date?: string // YYYY-MM-DD 형식
): Promise<MiniGasReading[]> => {
  const queryParams: Record<string, any> = {};
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  if (date !== undefined) queryParams.date = date;
  const response = await apiHelpers.get<MiniGasReading[]>(
    'miniLogs.gasReadings',
    { logId },
    queryParams
  );
  return extractApiData(response);
};

/**
 * 기기별 사용 가능한 날짜 목록 조회 (실제 데이터의 timestamp 기준)
 */
export const getAvailableDatesByDevice = async (serialNumber: string): Promise<string[]> => {
  const queryParams = {
    serial_number: serialNumber,
  };
  const response = await apiHelpers.get<string[]>(
    'miniLogs.availableDatesByDevice',
    {},
    queryParams
  );
  return extractApiData(response);
};

/**
 * 기기별 Gas Reading 조회 (serial_number + date 기반)
 */
export const getGasReadingsByDevice = async (
  serialNumber: string,
  date: string, // YYYY-MM-DD 형식 (필수)
  limit?: number,
  offset?: number
): Promise<MiniGasReading[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    date: date,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<MiniGasReading[]>(
    'miniLogs.gasReadingsByDevice',
    {},
    queryParams
  );
  return extractApiData(response);
};

/**
 * Calibration Log 데이터 타입 (MINI - 단일 센서)
 */
/**
 * 기기별 Calibration Log 조회 (serial_number + 기간)
 */
export const getCalibrationLogsByDevice = async (
  serialNumber: string,
  startDate: string, // YYYY-MM-DD 형식 (필수)
  endDate: string, // YYYY-MM-DD 형식 (필수)
  limit?: number,
  offset?: number
): Promise<MiniCalibrationLog[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    start_date: startDate,
    end_date: endDate,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<MiniCalibrationLog[]>(
    'miniLogs.calibrationLogs',
    {},
    queryParams
  );
  return extractApiData(response);
};

/**
 * Gas Alarm Log 데이터 타입 (MINI)
 */
/**
 * 기기별 Gas Alarm Log 조회 (serial_number + 기간)
 */
export const getGasAlarmLogsByDevice = async (
  serialNumber: string,
  startDate: string, // YYYY-MM-DD 형식 (필수)
  endDate: string, // YYYY-MM-DD 형식 (필수)
  limit?: number,
  offset?: number
): Promise<MiniGasAlarmLog[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    start_date: startDate,
    end_date: endDate,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<MiniGasAlarmLog[]>(
    'miniLogs.gasAlarmLogs',
    {},
    queryParams
  );
  return extractApiData(response);
};

/**
 * Event Log 데이터 타입 (MINI)
 */
/**
 * 기기별 Event Log 조회 (serial_number + 기간)
 */
export const getEventLogsByDevice = async (
  serialNumber: string,
  startDate: string, // YYYY-MM-DD 형식 (필수)
  endDate: string, // YYYY-MM-DD 형식 (필수)
  limit?: number,
  offset?: number
): Promise<MiniEventLog[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    start_date: startDate,
    end_date: endDate,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<MiniEventLog[]>('miniLogs.eventLogs', {}, queryParams);
  return extractApiData(response);
};

/**
 * Configuration Log 데이터 타입 (MINI)
 */
/**
 * 기기별 Configuration Log 조회
 */
export const getConfigurationLogsByDevice = async (
  serialNumber: string,
  startDate: string,
  endDate: string,
  limit?: number,
  offset?: number
): Promise<MiniConfigurationLog[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    start_date: startDate,
    end_date: endDate,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<MiniConfigurationLog[]>(
    'miniLogs.configurationLogs',
    {},
    queryParams
  );
  return extractApiData(response);
};
